import React, { Component, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { crypto } from '@binance-chain/javascript-sdk'

import Coinomi, { CoinomiDexBridge } from '../../utils/integrations/coinomiBridge'
import { Consumer as BNCClientConsumer } from '../context/BNCClientProvider'
import Modal from '../../components/common/Modal'

const ModalBody = styled.div`
  color: inherit;
  text-align: center;
`

/**
 * The Coinomi signing delegate.
 * @param  {preSignCb}  function
 * @param  {postSignCb} function
 * @param  {errCb} function
 * @return {function}
 */
export const CoinomiSigningDelegate = (preSignCb, postSignCb, errCb, ...emulateArgs) => async (
  tx,
  signMsg
) => {
  let pubKeyHex
  let sigHex
  const signBytes = tx.getSignBytes(signMsg)
  preSignCb && preSignCb(signBytes)
  const coinomi = Coinomi.getInstance(...emulateArgs)
  if (!coinomi.hasIntegration()) {
    errCb && errCb(new Error('Coinomi was not found!'))
    return tx
  }
  try {
    const { signature, publicKey } = await coinomi.signTransaction(signBytes.toString('hex'))
    pubKeyHex = publicKey
    sigHex = signature
    postSignCb && postSignCb(publicKey, signature)
  } catch (err) {
    console.error('Coinomi error!', err)
    if (err.code === CoinomiDexBridge.ERRCODE_USER_CANCEL) {
      postSignCb && postSignCb()
      errCb && errCb(new Error('User Cancelled'))
      return tx
    }
    errCb && errCb(err)
    return tx
  }
  if (pubKeyHex && sigHex) {
    const pubKey = crypto.getPublicKey(pubKeyHex)
    return tx.addSignature(pubKey, Buffer.from(sigHex, 'hex'))
  }
  errCb && errCb(new Error('Coinomi responded without a pubkey or signature'))
  return tx
}

const CoinomiDelegateConfigurator = ({
  bncClient,
  preSignCb,
  postSignCb,
  errCb,
  isCoinomi,
  isCoinomiEmulate,
}) => {
  // similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    if (!bncClient || !isCoinomi) return
    bncClient.setSigningDelegate(
      CoinomiSigningDelegate(preSignCb, postSignCb, errCb, isCoinomiEmulate)
    )
  })
  return <div />
}

CoinomiDelegateConfigurator.propTypes = {
  bncClient: PropTypes.object.isRequired,
  preSignCb: PropTypes.func,
  postSignCb: PropTypes.func,
  errCb: PropTypes.func,
  isCoinomi: PropTypes.bool,
  isCoinomiEmulate: PropTypes.bool,
}

CoinomiDelegateConfigurator.defaultProps = {
  preSignCb: () => {},
  postSignCb: () => {},
  errCb: () => {},
  isCoinomi: false,
  isCoinomiEmulate: false,
}

class CoinomiModalHost extends Component {
  static propTypes = {
    isCoinomi: PropTypes.bool,
    isCoinomiEmulate: PropTypes.bool,
  }

  static defaultProps = {
    isCoinomi: false,
    isCoinomiEmulate: false,
  }

  state = { error: null, showModal: false }

  _showSignModal = () => {
    this.setState({ showModal: true })
  }

  _hideSignModal = () => {
    this.setState({ showModal: false })
  }

  _showErrorInModal = err => {
    this.setState({ showModal: false, error: err })
  }

  _clearError = () => {
    this.setState({ error: null })
  }

  render() {
    const { error, showModal } = this.state
    const { isCoinomi, isCoinomiEmulate } = this.props
    return (
      <>
        <BNCClientConsumer>
          {bncClient => (
            <CoinomiDelegateConfigurator
              bncClient={bncClient}
              preSignCb={this._showSignModal}
              postSignCb={this._hideSignModal}
              errCb={this._showErrorInModal}
              isCoinomi={isCoinomi}
              isCoinomiEmulate={isCoinomiEmulate}
            />
          )}
        </BNCClientConsumer>
        {showModal ? (
          <Modal title="Ready to Confirm">
            <ModalBody>Please confirm the transaction in Coinomi.</ModalBody>
          </Modal>
        ) : null}
        {error ? (
          <Modal close={this._clearError} title="Coinomi Error">
            <ModalBody>
              <p>A Coinomi integration error occurred:</p>
              <p>
                <i>
                  {error.code}:{error.message}
                </i>
              </p>
              <p>Please ensure that Coinomi is open and working.</p>
              <p style={{ marginTop: '1rem' }}>
                <a href="/unlock" rel="noopener">
                  Click here to unlock another wallet
                </a>
              </p>
            </ModalBody>
          </Modal>
        ) : null}
      </>
    )
  }
}

const mapStateToProps = ({
  account: {
    flags: { isCoinomi, isCoinomiEmulate },
  },
}) => ({
  isCoinomi,
  isCoinomiEmulate,
})

export default connect(mapStateToProps)(CoinomiModalHost)
